import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Activity, BarChart3, Building2, Clock3, LayoutDashboard, Mail, Settings, Sliders, Tag, Target, Users, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCurrentUser } from '@hooks/useAuth';
import { initials } from '@lib/format';
import { brand } from '@/config/brand';

type SidebarProps = {
  isOpen: boolean;
  onClose: () => void;
};

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
};

const navigation: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/app-health', label: 'App Health', icon: Activity },
  { to: '/apollo', label: 'Apollo', icon: Users },
  { to: '/leads', label: 'Leads', icon: Target },
  { to: '/accounts', label: 'Accounts', icon: Building2 },
  { to: '/opportunities', label: 'Opportunities', icon: BarChart3 },
  { to: '/cadence', label: 'Cadence', icon: Clock3 },
  { to: '/templates', label: 'Templates', icon: Mail },
  { to: '/promotions', label: 'Promotions', icon: Tag },
  { to: '/features', label: 'Features', icon: Sliders },
  { to: '/settings', label: 'Settings', icon: Settings },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const { data: currentUser } = useCurrentUser();
  const name = currentUser ? `${currentUser.first_name} ${currentUser.last_name}`.trim() : 'Founder';

  return (
    <>
      {isOpen ? <div className="fixed inset-0 bg-black/60 backdrop-blur-sm lg:hidden z-30" onClick={onClose} aria-hidden="true" /> : null}

      <aside
        className={clsx(
          'fixed left-0 top-0 h-screen w-64 z-40',
          'bg-surface-base border-r border-border-default',
          'flex flex-col',
          'transform transition-transform duration-200',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <img src={brand.logoUrl} alt={`${brand.name} logo`} className="h-8 w-8 object-contain" />
            <div>
              <p className="text-sm font-semibold text-content-secondary">{brand.name}</p>
              <h1 className="text-sm text-content-muted uppercase tracking-wider">Founder CRM</h1>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="lg:hidden p-1.5 hover:bg-surface-raised rounded-lg transition-colors text-content-muted hover:text-content-primary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto" aria-label="CRM Navigation">
          {navigation.map(({ to, label, icon: Icon }) => {
            const isActive = to === '/'
              ? location.pathname === '/'
              : location.pathname === to || location.pathname.startsWith(`${to}/`);

            return (
              <Link
                key={to}
                to={to}
                onClick={onClose}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-100 text-primary-600'
                    : 'text-content-secondary hover:bg-surface-raised hover:text-content-primary'
                )}
              >
                <Icon size={18} />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border-subtle">
          {currentUser && (
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                {initials(name)}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-content-primary truncate">
                  {currentUser.first_name} {currentUser.last_name}
                </p>
                <p className="text-xs text-content-muted truncate">{currentUser.email}</p>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
